import { useEffect, useState } from "react";

import { ActionIcon, Box, Flex, Group, Select, Stack } from "@mantine/core";
import { Dropzone, PDF_MIME_TYPE, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { IconChevronDown, IconChevronUp, IconDownload, IconTrash, IconUpload } from "@tabler/icons-react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { Text } from "components";
import { ServiceConstants } from "constant";
import { BankSelectionService } from "services";
import { Bank, BankAttachmentResponse, DocLanguage } from "types";

const { REQUEST_BANK_ATTACHMENT_LIST_ID, REQUEST_BANK_ATTACHMENT_UPLOAD_ID } = ServiceConstants;

const TITLE_COLOR = "rgba(28, 54, 184, 1)";

const DOC_LANGUAGE_OPTIONS: { value: DocLanguage; label: string }[] = [
  { value: "DE", label: "DE" },
  { value: "FR", label: "FR" },
  { value: "IT", label: "IT" },
  { value: "EN", label: "EN" },
];

interface DetailsSectionProps {
  bank: Bank;
  bankCreditRequestId: number;
  docLanguage?: DocLanguage;
  onDocLanguageChange?: (language: DocLanguage) => void;
}

export const DetailsSection = ({
  bank,
  bankCreditRequestId,
  docLanguage,
  onDocLanguageChange,
}: DetailsSectionProps) => {
  const { t } = useTranslation();
  const [mainDocument, setMainDocument] = useState<BankAttachmentResponse | undefined>();
  const [additionalDocuments, setAdditionalDocuments] = useState<BankAttachmentResponse[]>([]);
  const [isLanguageSelectOpen, setIsLanguageSelectOpen] = useState(false);

  const attachmentsResponse = useSelector(
    (state: any) => state.service.object[REQUEST_BANK_ATTACHMENT_LIST_ID]
  );

  const uploadResponse = useSelector(
    (state: any) => state.service.object[REQUEST_BANK_ATTACHMENT_UPLOAD_ID]
  );

  useEffect(() => {
    if (bankCreditRequestId && bank) {
      BankSelectionService.bankAttachmentsAll(bankCreditRequestId, bank);
    }
  }, [bankCreditRequestId, bank]);

  useEffect(() => {
    const data = attachmentsResponse?.data;
    if (!data) return;
    const attachments: BankAttachmentResponse[] = Array.isArray(data)
      ? data
      : Array.isArray(data.attachments)
      ? data.attachments
      : [];

    const main = attachments.find((a) => a.subtype === "MAIN");
    const additional = attachments.filter((a) => a.subtype === "ADDITIONAL");

    setMainDocument(main);
    setAdditionalDocuments(additional);
  }, [attachmentsResponse]);

  useEffect(() => {
    if (uploadResponse?.data && bankCreditRequestId && bank) {
      BankSelectionService.bankAttachmentsAll(bankCreditRequestId, bank);
    }
  }, [uploadResponse, bankCreditRequestId, bank]);

  const handleMainDocumentUpload = (file: File) => {
    if (bankCreditRequestId) {
      BankSelectionService.bankAttachmentUploadMain(bankCreditRequestId, bank, file);
    }
  };

  const handleAdditionalDocumentsUpload = (files: File[]) => {
    if (bankCreditRequestId) {
      BankSelectionService.bankAttachmentUploadMultiple(bankCreditRequestId, bank, files);
    }
  };

  const handleDelete = (id: string) => {
    // Optimistic update - odmah ukloni iz UI
    if (mainDocument?.id === id) {
      setMainDocument(undefined);
    } else {
      setAdditionalDocuments((prev) => prev.filter((doc) => doc.id !== id));
    }
    BankSelectionService.bankAttachmentDelete(id);
  };

  const handleDownload = (attachment: BankAttachmentResponse) => {
    if (attachment.path) {
      window.open(attachment.path, "_blank");
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Box
      p="md"
      style={{
        border: "1px solid rgba(50, 51, 67, 0.15)",
        borderRadius: 12,
        height: "100%",
        backgroundColor: "rgba(241, 242, 244, 1)",
      }}
    >
      <Text fw={600} c={TITLE_COLOR} mb="md">
        {t("bankSelection.details.title")}
      </Text>

      <Stack gap="md">
        {/* Glavna dokumentacija */}
        <Box>
          <Text size="sm" fw={500} mb="xs">
            {t("bankSelection.details.mainDocument")}
          </Text>

          {mainDocument ? (
            <Flex
              align="center"
              justify="space-between"
              p="xs"
              style={{
                border: "1px solid rgba(50, 51, 67, 0.15)",
                borderRadius: 8,
                backgroundColor: "white",
              }}
            >
              <Text size="sm" style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                {mainDocument.name}
              </Text>
              <Group gap="xs">
                {mainDocument.size && (
                  <Text size="xs" c="dimmed">
                    {formatFileSize(mainDocument.size)}
                  </Text>
                )}
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={() => handleDownload(mainDocument)}
                >
                  <IconDownload size={16} />
                </ActionIcon>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="sm"
                  onClick={() => handleDelete(mainDocument.id)}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            </Flex>
          ) : (
            <Dropzone
              onDrop={(files) => {
                if (files[0]) {
                  handleMainDocumentUpload(files[0]);
                }
              }}
              maxSize={30 * 1024 * 1024}
              accept={PDF_MIME_TYPE}
              multiple={false}
              styles={{
                root: {
                  border: "1px dashed rgba(50, 51, 67, 0.3)",
                  borderRadius: 8,
                  backgroundColor: "white",
                  padding: 8,
                  maxWidth: 250,
                },
              }}
            >
              <Flex align="center" gap="xs">
                <IconUpload size={16} color="gray" />
                <Text size="sm" c="dimmed">
                  {t("bankSelection.details.uploadMain")}
                </Text>
              </Flex>
            </Dropzone>
          )}
        </Box>

        {/* Dodatna dokumentacija */}
        <Box>
          <Text size="sm" fw={500} mb="xs">
            {t("bankSelection.details.additionalDocuments")}
          </Text>

          <Stack gap="xs">
            {additionalDocuments.map((doc) => (
              <Flex
                key={doc.id}
                align="center"
                justify="space-between"
                p="xs"
                style={{
                  border: "1px solid rgba(50, 51, 67, 0.15)",
                  borderRadius: 8,
                  backgroundColor: "white",
                }}
              >
                <Text size="sm" style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                  {doc.name}
                </Text>
                <Group gap="xs">
                  {doc.size && (
                    <Text size="xs" c="dimmed">
                      {formatFileSize(doc.size)}
                    </Text>
                  )}
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={() => handleDownload(doc)}
                  >
                    <IconDownload size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="sm"
                    onClick={() => handleDelete(doc.id)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Flex>
            ))}

            <Dropzone
              onDrop={(files) => {
                handleAdditionalDocumentsUpload(files);
              }}
              maxSize={30 * 1024 * 1024}
              accept={[...PDF_MIME_TYPE, ...IMAGE_MIME_TYPE]}
              multiple
              styles={{
                root: {
                  border: "1px dashed rgba(50, 51, 67, 0.3)",
                  borderRadius: 8,
                  backgroundColor: "white",
                  padding: 8,
                  maxWidth: 250,
                },
              }}
            >
              <Flex align="center" gap="xs">
                <IconUpload size={16} color="gray" />
                <Text size="sm" c="dimmed">
                  {t("bankSelection.details.uploadAdditional")}
                </Text>
              </Flex>
            </Dropzone>
          </Stack>
        </Box>

        {/* Jezik dokumentacije */}
        <Flex align="center" gap="xs">
          <Text size="sm" fw={500}>
            {`${t("bankSelection.details.docLanguage")}:`}
          </Text>
          <Select
            value={docLanguage || null}
            onChange={(value) => value && onDocLanguageChange?.(value as DocLanguage)}
            data={DOC_LANGUAGE_OPTIONS}
            placeholder="-"
            size="xs"
            allowDeselect={false}
            withCheckIcon={false}
            rightSection={
              isLanguageSelectOpen ? (
                <IconChevronUp size={14} />
              ) : (
                <IconChevronDown size={14} />
              )
            }
            onDropdownOpen={() => setIsLanguageSelectOpen(true)}
            onDropdownClose={() => setIsLanguageSelectOpen(false)}
            styles={{
              input: {
                width: 70,
                minHeight: 28,
                borderRadius: 6,
              },
            }}
          />
        </Flex>
      </Stack>
    </Box>
  );
};
